import React from 'react'
import Image from 'next/image'
import { s3Static } from '../../../../utils/constants'
import { motion } from 'framer-motion'
import { FaStar, FaQuoteLeft, FaTrophy, FaHome } from 'react-icons/fa'
import { HiLightningBolt } from 'react-icons/hi'
import Link from 'next/link'

export const ClientStoryKimberly = () => {
    return (
        <section className="py-12 bg-white">
            <div className="max-w-6xl mx-auto px-4">
                {/* Section Header */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mb-10"
                >
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <FaTrophy className="text-yellow-500 text-2xl" />
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                            Home Training Success Story
                        </h2>
                    </div>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        See how Kimberly achieved her fitness goals with convenient home training sessions
                    </p>
                </motion.div>

                {/* Compact Story Card */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                    className="grid md:grid-cols-2 gap-8 bg-linear-to-br from-blue-50 to-cyan-50 rounded-3xl p-6 md:p-8 shadow-xl border border-blue-100"
                >
                    {/* Left: Image, Stats & Client Info */}
                    <div className="space-y-4">
                        <div className="relative rounded-2xl overflow-hidden shadow-lg h-96 md:h-120">
                            <Image
                                src={s3Static + "personal-trainer/generated/gerald-training-kimberly.png"}
                                fill
                                style={{ objectFit: 'cover', objectPosition: 'top' }}
                                alt="Kimberly training with Personal Trainer Gerald at home in Singapore"
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                            {/* Floating Badge */}
                            <div className="absolute top-4 left-4 bg-white rounded-full px-3 py-1 shadow-lg flex items-center gap-2">
                                <FaHome className="text-blue-600" />
                                <span className="font-bold text-sm text-gray-800">Home Training</span>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded-xl p-3 text-center shadow-md">
                                <div className="text-2xl font-bold text-blue-600">8kg</div>
                                <div className="text-xs text-gray-600">Lost</div>
                            </div>
                            <div className="bg-white rounded-xl p-3 text-center shadow-md">
                                <div className="text-2xl font-bold text-green-600">3 Months</div>
                                <div className="text-xs text-gray-600">Duration</div>
                            </div>
                        </div>                        
                    </div>

                    {/* Right: Story Content */}
                    <div className="space-y-4">
                        {/* Client Info */}
                        <div className="bg-white rounded-xl p-4 shadow-md">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Kimberly Lim</h3>
                                    <p className="text-sm text-gray-600">Corporate Executive, 29 years old</p>
                                    <p className="text-xs text-gray-500 mt-1">📍 Training Location: Home, Bukit Timah</p>
                                </div>
                                <div className="flex gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <FaStar key={i} className="text-amber-400 text-sm" />
                                    ))}
                                </div>
                            </div>
                            
                            {/* Quote */}
                            <div className="relative bg-blue-50 rounded-lg p-3">
                                <FaQuoteLeft className="text-blue-200 text-xl absolute top-2 left-2 opacity-50" />
                                <p className="text-sm text-gray-700 italic pl-6 leading-relaxed">
                                    &quot;As a busy executive, home training with Gerald was perfect. No commute time, just results. He transformed my living room into a gym and my body into one I&apos;m proud of!&quot;
                                </p>
                            </div>
                        </div>
                        {/* Journey Summary */}
                        <div className="bg-white rounded-xl p-4 shadow-md space-y-3">
                            <h4 className="font-bold text-gray-900 flex items-center gap-2">
                                <HiLightningBolt className="text-yellow-500" />
                                Transformation Journey
                            </h4>
                            <div className="space-y-2 text-sm text-gray-700">
                                <p>
                                    <strong>Challenge:</strong> Kimberly struggled with work-life balance and post-pregnancy weight. Long hours meant gym time was impossible.
                                </p>
                                <p>
                                    <strong>Solution:</strong> Gerald brought the gym to her. Home sessions at 6:30 AM before work, using minimal equipment for maximum results.
                                </p>
                                <p>
                                    <strong>Results:</strong> Lost 8kg, improved strength and energy levels. Now maintains her fitness independently with Gerald&apos;s guidance.
                                </p>
                            </div>
                        </div>

                        {/* Trainer Info */}
                        <div className="bg-linear-to-r from-blue-600 to-cyan-600 rounded-xl p-4 shadow-md text-white">
                            <div className="flex items-center gap-3">
                                <div className="relative w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg shrink-0">
                                    <Image
                                        src={s3Static + "personal-trainer/generated/Gerald.jpeg"}
                                        fill
                                        style={{ objectFit: 'cover' }}
                                        alt="Personal Trainer Gerald Tan"
                                        sizes="64px"
                                    />
                                </div>
                                <div className="flex-grow">
                                    <h5 className="font-bold text-lg">Gerald Tan</h5>
                                    <p className="text-blue-100 text-xs">NSCA Certified (CSCS)</p>
                                    <p className="text-blue-100 text-xs">Sports Science Specialist</p>
                                    <div className="flex gap-1 mt-1">
                                        {[...Array(5)].map((_, i) => (
                                            <FaStar key={i} className="text-yellow-300 text-xs" />
                                        ))}
                                        <span className="ml-1 text-xs">4.8/5.0</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Key Benefits */}
                        <div className="bg-green-50 rounded-xl p-4 shadow-md">
                            <h4 className="font-bold text-gray-900 mb-2 text-sm">Home Training Benefits:</h4>
                            <ul className="space-y-1 text-xs text-gray-700">
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5">✓</span>
                                    <span>No commute - save 1-2 hours per session</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5">✓</span>
                                    <span>Privacy and comfort of your own space</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5">✓</span>
                                    <span>Flexible scheduling around your lifestyle</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5">✓</span>
                                    <span>Trainer brings equipment - no gym membership needed</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </motion.div>

                {/* CTA */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    viewport={{ once: true }}
                    className="text-center mt-8"
                >
                    <Link 
                        href="/sg/personal-trainer"
                        className="inline-block bg-linear-to-r from-blue-600 to-cyan-600 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                        Start Your Home Training Journey →
                    </Link>
                    <p className="text-sm text-gray-600 mt-3">
                        Convenient, effective, and personalized for your home
                    </p>
                </motion.div>

                {/* YMYL Disclaimer */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    viewport={{ once: true }}
                    className="mt-8 bg-gray-50 rounded-xl p-4 border-l-4 border-blue-500"
                >
                    <p className="text-xs text-gray-700 leading-relaxed">
                        <strong>Disclaimer:</strong> Individual results vary. Kimberly&apos;s success resulted from dedicated training with a certified trainer, proper nutrition, and lifestyle modifications over 5 months. StringsSG trainers are qualified fitness professionals, not medical doctors. Consult your physician before starting any exercise program. Results depend on individual commitment, starting fitness level, and health status.
                    </p>
                </motion.div>
            </div>
        </section>
    )
}
